/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {Navigate} from 'react-router-dom'
import axios from 'axios'
import './index.scss'

import {KTSVG} from '../../../_metronic/helpers'
import {getAdminToken} from '../../modules/admin/auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

type Contact = {
  _id: string
  label: string
  whatsapp: string
  whatsapp_intl: string
  message: string
  code: string
  link: string
  clicks: number
  disabled: boolean
}

const ContactsWrapper: FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [copiedId, setCopiedId] = useState('')

  // Admin gate: null = checking, true/false = result
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  // form state — also used for editing (editingId set => PUT)
  const [editingId, setEditingId] = useState('')
  const [label, setLabel] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [message, setMessage] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/api/contacts`)
      setContacts(res.data?.contacts || [])
      setError('')
    } catch (e) {
      setError('שגיאה בטעינת אנשי הקשר')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const adminToken = getAdminToken()
        const headers: Record<string, string> = {}
        if (adminToken) {
          headers['Authorization'] = `Bearer ${adminToken}`
        }
        const res = await axios.get(`${API_URL}/admin/check-auth`, {headers, withCredentials: true})
        setIsAdmin(res.data.loggedIn === true)
      } catch {
        setIsAdmin(false)
      }
    }
    checkAdmin()
  }, [])

  useEffect(() => {
    if (isAdmin) load()
  }, [isAdmin])

  const resetForm = () => {
    setEditingId('')
    setLabel('')
    setWhatsapp('')
    setMessage('')
  }

  const startEdit = (c: Contact) => {
    setEditingId(c._id)
    setLabel(c.label)
    setWhatsapp(c.whatsapp)
    setMessage(c.message || '')
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const save = async () => {
    if (!whatsapp.trim()) {
      setError('יש להזין מספר וואטסאפ')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/contacts/${editingId}`, {label, whatsapp, message})
      } else {
        await axios.post(`${API_URL}/api/contacts`, {label, whatsapp, message})
      }
      resetForm()
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'שמירה נכשלה — בדקו את מספר הוואטסאפ')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (c: Contact) => {
    if (!window.confirm(`למחוק את איש הקשר${c.label ? ` "${c.label}"` : ''}?`)) return
    try {
      await axios.delete(`${API_URL}/api/contacts/${c._id}`)
      await load()
    } catch (e) {
      setError('מחיקה נכשלה')
    }
  }

  const copyLink = async (c: Contact) => {
    try {
      await navigator.clipboard.writeText(c.link)
      setCopiedId(c._id)
      setTimeout(() => setCopiedId(''), 1500)
    } catch (e) {
      window.prompt('העתיקו את הקישור:', c.link)
    }
  }

  if (isAdmin === null) {
    return <div className='text-center text-muted py-10'>טוען...</div>
  }
  if (isAdmin === false) {
    return <Navigate to='/campaigns' replace />
  }

  return (
    <>
      <div className='d-flex flex-wrap flex-stack mb-6'>
        <h3 className='fw-bolder my-2'>אנשי קשר</h3>
      </div>

      {/* Add / edit form */}
      <div className='card mb-6'>
        <div className='card-body'>
          <h5 className='fw-bold mb-4'>{editingId ? 'עריכת איש קשר' : 'הוספת איש קשר'}</h5>
          <div className='row g-4'>
            <div className='col-md-6'>
              <label className='form-label fs-7 text-muted'>שם / תיאור</label>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='לדוגמה: אבי - מכירות'
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label fs-7 text-muted'>מספר וואטסאפ</label>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='054-994-6617'
                dir='ltr'
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </div>
            <div className='col-12'>
              <label className='form-label fs-7 text-muted'>הודעת פתיחה לוואטסאפ (אופציונלי)</label>
              <textarea
                className='form-control form-control-solid'
                placeholder='הטקסט שיופיע מוכן בהודעת הוואטסאפ של הלקוח, למשל: היי, ראיתי את המודעה ואשמח לפרטים'
                rows={2}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className='col-12 d-flex gap-2'>
              <button className='btn btn-primary' onClick={save} disabled={saving}>
                {saving ? 'שומר...' : editingId ? 'שמירה' : 'הוספה'}
              </button>
              {editingId && (
                <button className='btn btn-light' onClick={resetForm} disabled={saving}>
                  ביטול
                </button>
              )}
            </div>
          </div>
          {error && <div className='text-danger mt-3 fw-semibold'>{error}</div>}
        </div>
      </div>

      {/* List */}
      <div className='card'>
        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>שם / תיאור</th>
                  <th className='min-w-150px'>וואטסאפ</th>
                  <th className='min-w-200px'>הודעת פתיחה</th>
                  <th className='min-w-250px'>קישור לשיתוף</th>
                  <th className='min-w-80px text-center'>קליקים</th>
                  <th className='min-w-100px text-end'>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className='text-center text-muted py-10'>
                      טוען...
                    </td>
                  </tr>
                )}
                {!loading && contacts.length === 0 && (
                  <tr>
                    <td colSpan={6} className='text-center text-muted py-10'>
                      עדיין לא הוספתם אנשי קשר. הוסיפו את הראשון למעלה.
                    </td>
                  </tr>
                )}
                {!loading &&
                  contacts.map((c) => (
                    <tr key={c._id}>
                      <td>
                        <span className='text-dark fw-bold fs-6'>{c.label || '—'}</span>
                      </td>
                      <td>
                        <span className='text-dark fw-bold fs-6' dir='ltr'>
                          {c.whatsapp}
                        </span>
                      </td>
                      <td>
                        <span className='text-muted d-inline-block text-truncate' style={{maxWidth: 220}} title={c.message}>
                          {c.message || '—'}
                        </span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center gap-2'>
                          <a href={c.link} target='_blank' rel='noopener noreferrer' dir='ltr' className='text-primary text-truncate' style={{maxWidth: 260}}>
                            {c.link}
                          </a>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            title='העתקת קישור'
                            onClick={() => copyLink(c)}
                          >
                            {copiedId === c._id ? (
                              <span className='text-success fw-bold fs-8'>✓</span>
                            ) : (
                              <KTSVG path='/media/icons/duotune/general/gen054.svg' className='svg-icon-3' />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-primary'>{c.clicks}</span>
                      </td>
                      <td>
                        <div className='d-flex justify-content-end flex-shrink-0'>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                            title='עריכה'
                            onClick={() => startEdit(c)}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-3' />
                          </button>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                            title='מחיקה'
                            onClick={() => remove(c)}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

export {ContactsWrapper}
